import { supabase } from '@/integrations/supabase/client';
import { ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { UserVoiceProfile, UserVoiceProfileInsert, UserVoiceProfileUpdate } from '@/types/database';

const serviceLogger = logger.child({ service: 'UserVoiceService' });

export class UserVoiceService {
  /**
   * Get user's voice profile
   */
  static async getVoiceProfile(userId: string, organizationId: string): Promise<UserVoiceProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_voice_profiles')
        .select('*')
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      serviceLogger.error('Failed to fetch voice profile', { error, userId, organizationId });
      throw new ServiceError('Failed to fetch voice profile', 'USER_VOICE_FETCH_ERROR', 500, { originalError: error });
    }
  }

  /**
   * Create user voice profile
   */
  static async createVoiceProfile(data: UserVoiceProfileInsert): Promise<UserVoiceProfile> {
    try {
      const { data: profile, error } = await supabase
        .from('user_voice_profiles')
        .insert(data)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!profile) throw new Error('No data returned from insert');

      serviceLogger.info('Voice profile created', { userId: data.user_id, organizationId: data.organization_id });
      return profile;
    } catch (error) {
      serviceLogger.error('Failed to create voice profile', { error, data });
      throw new ServiceError('Failed to create voice profile', 'USER_VOICE_CREATE_ERROR', 500, { originalError: error });
    }
  }

  /**
   * Update user voice profile
   */
  static async updateVoiceProfile(
    userId: string,
    organizationId: string,
    updates: UserVoiceProfileUpdate
  ): Promise<UserVoiceProfile> {
    try {
      const { data: profile, error } = await supabase
        .from('user_voice_profiles')
        .update(updates)
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!profile) throw new Error('No data returned from update');

      serviceLogger.info('Voice profile updated', { userId, organizationId });
      return profile;
    } catch (error) {
      serviceLogger.error('Failed to update voice profile', { error, userId, organizationId });
      throw new ServiceError('Failed to update voice profile', 'USER_VOICE_UPDATE_ERROR', 500, { originalError: error });
    }
  }

  /**
   * Upsert user voice profile (create or update)
   */
  static async upsertVoiceProfile(data: UserVoiceProfileInsert): Promise<UserVoiceProfile> {
    try {
      const { data: profile, error } = await supabase
        .from('user_voice_profiles')
        .upsert(data, { onConflict: 'user_id,organization_id' })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!profile) throw new Error('No data returned from upsert');

      serviceLogger.info('Voice profile upserted', { userId: data.user_id, organizationId: data.organization_id });
      return profile;
    } catch (error) {
      serviceLogger.error('Failed to upsert voice profile', { error, data });
      throw new ServiceError('Failed to upsert voice profile', 'USER_VOICE_UPSERT_ERROR', 500, { originalError: error });
    }
  }
}
